import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Stack,
  TextField,
  IconButton,
  Typography,
  Box,
  Pagination,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  Skeleton,
  Snackbar,
  Alert,
  Tab,
  Tabs
} from '@mui/material';
import { 
  Search, 
  FileDownload,
  PictureAsPdf,
  Print,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import AdminDashboard from './AdminDashboard';
import { styled } from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1),
  fontSize: '0.875rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const ProductApproval = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const userData = JSON.parse(sessionStorage.getItem("admin_user"));
  const [filterStatus, setFilterStatus] = useState('ALL');
  const { UserID, AuthCode } = userData.BoAcntLst[0]; 
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
 
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://testing.esnep.com/magnessa/api/bo/transaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          "ComID": "EH25",
          "UserID": UserID,
          "Flag": "S",
          "TransStatus": filterStatus === 'ALL' ? '-1' : filterStatus === 'Pending' ? 'P' : 'R',
          "FromDate": "2024-06-01",
          "ToDate": "2025-06-01",
          "ReferenceID": "-1",
          "AuthCode": AuthCode
        })
      });
      const result = await response.json();
      if (result.StatusCode === 200) {
        setData(result.BoProdLst);
      } else {
        console.error('Error fetching data:', result.Message);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const handleTabChange = (event, newValue) => {
    setFilterStatus(newValue);
  };

   // Modify handleStatusChange to handle confirmed actions
   const handleStatusChange = async (confirmed) => {
    if (!confirmed) {
      setConfirmOpen(false);
      return;
    }

    try {
      const response = await fetch('https://testing.esnep.com/magnessa/api/bo/transaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          "ComID": "EH25",
          "UserID": UserID,
          "TransStatus": selectedAction,
          "Flag": "US",
          "TransID": selectedRow.TransID,
          "ReferenceID": selectedRow.ReferralID,
          "AuthCode": AuthCode
        })
      });

      const result = await response.json();
      if (result.StatusCode === 200) {
        setData(prevData =>
          prevData.map(item =>
            item.TransID === selectedRow.TransID ? { 
              ...item, 
              TransStatus: selectedAction === "A" ? "Approved" : "Rejected"
            } : item
          )
        );
        setSnackbar({ 
          open: true, 
          message: `Transaction ${selectedAction === "A" ? "Approved" : "Rejected"} successfully!`,
          severity: 'success' 
        });
      } else {
        setSnackbar({ open: true, message: result.Message || 'Failed to update status.', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error updating status.', severity: 'error' });
    } finally {
      setConfirmOpen(false);
    }
  };

    // Add confirmation handler
    const handleConfirmation = (row, action) => {
      setSelectedRow(row);
      setSelectedAction(action);
      setConfirmOpen(true);
    };
  

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredData = data.filter(item => 
    item.FullName && item.FullName.toLowerCase().includes(searchTerm.toLowerCase())
);


  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig.key) {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const paginatedData = sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleDelete = () => {
    setData(prev => prev.filter(admin_user => admin_user.id !== selectedUser.id));
    setOpenDialog(false);
  };
  console.log("Paginated Data:", data);

  return (
    <AdminDashboard>
      <Box component="main" sx={{ 
        flexGrow: 1,
        p: 3,
        backgroundColor: "#f7f7f7",
        minHeight: '100vh'
      }}>
    
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Product Approval
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{lineHeight:"2"}}>
            Manage and review the product and approve the request.
          </Typography>
        </Box>

        <Paper sx={{ 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={filterStatus} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                }
              }}
            >
              <Tab label="ALL" value="ALL" />
              <Tab label="Pending" value="Pending" />
              <Tab label="Rejected" value="Rejected" />
            </Tabs>
          </Box>
          <Box sx={{ 
            p: 2, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
                sx: { borderRadius: 2 }
              }}
              sx={{ width: 300 }}
            />

            <Stack direction="row" spacing={1}>
              <Tooltip title="Export to Excel">
                <IconButton size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <FileDownload fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to PDF">
                <IconButton size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <PictureAsPdf fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <Print fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
            <TableHead>
  <TableRow>
    {['Transaction ID', 'Full Name', 'Total Products', 'Total PV', 'Total Amount']
      .concat(filterStatus === 'ALL' ? ['Status'] : []) // Add "Status" only if "ALL" is selected
      .map((header) => (
        <StyledTableCell 
          key={header}
          onClick={() => header !== 'Actions' && handleSort(header.toLowerCase().replace(' ', ''))}
          sx={{ 
            cursor: header !== 'Actions' ? 'pointer' : 'default',
            minWidth: 120,
            py: 1
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{header}</span>
            {sortConfig.key === header.toLowerCase().replace(' ', '') && (
              sortConfig.direction === 'asc' ? 
              <CheckCircle fontSize="small" sx={{ fontSize: '0.875rem' }} /> : 
              <Cancel fontSize="small" sx={{ fontSize: '0.875rem' }} />
            )}
          </Stack>
        </StyledTableCell>
      ))}
  </TableRow>
</TableHead>


              <TableBody>
  {loading ? (
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {Array.from({ length: 6 }).map((_, i) => (
          <StyledTableCell key={i}>
            <Skeleton variant="text" />
          </StyledTableCell>
        ))}
      </TableRow>
    ))
  ) : 
    data
      .filter((row) => {
        if (filterStatus === 'ALL') return true; // Show all records
        if (filterStatus === 'Pending') return row.TransStatus === 'Pending';
        if (filterStatus === 'Rejected') return row.TransStatus === 'Rejected';
        return false; // Default case (should not happen)
      })
      .map((row) => (
        <TableRow hover key={row.TransID} sx={{ 
          '&:last-child td': { borderBottom: 0 },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          }
        }}>
          <StyledTableCell align="left">{row.TransID}</StyledTableCell>
          <StyledTableCell>{row.FullName}</StyledTableCell>
          <StyledTableCell align="left">{row.NoProduct}</StyledTableCell>
          <StyledTableCell align="left">{row.TotalPV}</StyledTableCell>
          <StyledTableCell align="left">{row.TotalAmt}</StyledTableCell>

          {/* Show buttons only in "ALL" filter */}
      {filterStatus === 'ALL' && (
      <StyledTableCell>
      <Stack direction="row" spacing={1}>
        {/* Show Approve button only if status is NOT "Rejected" */}
        {row.TransStatus !== "Rejected" && (
          <Button
          variant="contained"
          size="small"
          color="success"
          onClick={() => handleConfirmation(row, "A")}
          sx={{ 
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            textTransform: 'none',
            boxShadow: 'none',
            '&:disabled': {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.contrastText
            }
          }}
          disabled={row.TransStatus === "Approved"}
        >
          {row.TransStatus === "Approved" ? "Approved" : "Approve"}
        </Button>
        )}
    
        {/* Show Reject button only if status is NOT "Approved" */}
        {row.TransStatus !== "Approved" && (
          
  <Button
  variant="contained"
  size="small"
  color="error"
  onClick={() => handleConfirmation(row, "R")}
  sx={{ 
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
    textTransform: 'none',
    boxShadow: 'none',
    '&:disabled': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText
    }
  }}
  disabled={row.TransStatus === "Rejected"}
>
  {row.TransStatus === "Rejected" ? "Rejected" : "Reject"}
</Button>
        )}
      </Stack>
    </StyledTableCell>
           )}
        </TableRow>
      ))
  }
</TableBody>

            </Table>
          </TableContainer>

          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedData.length} of {filteredData.length} results
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </Box>
        </Paper>

        <Dialog
    open={confirmOpen}
    onClose={() => setConfirmOpen(false)}
    maxWidth="xs"
    fullWidth
  >
    <DialogTitle>Confirm Action</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to {selectedAction === 'A' ? 'approve' : 'reject'} transaction?
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => handleStatusChange(false)}>Cancel</Button>
      <Button 
        onClick={() => handleStatusChange(true)} 
        color={selectedAction === 'A' ? 'success' : 'error'} 
        variant="contained"
      >
        Confirm 
      </Button>
    </DialogActions>
  </Dialog>
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminDashboard>
  );
};

export default ProductApproval;